export type DesignerRequestErrorCode =
  | 'aborted'
  | 'timeout'
  | 'network_error'
  | 'http_error'
  | 'empty_response'
  | 'invalid_json'
  | 'invalid_response';

export interface DesignerRequestErrorDetails {
  code: DesignerRequestErrorCode;
  operation: string;
  status: number;
  retryable: boolean;
  requestId: string;
}

export class DesignerRequestError extends Error implements DesignerRequestErrorDetails {
  readonly code: DesignerRequestErrorCode;
  readonly operation: string;
  readonly status: number;
  readonly retryable: boolean;
  readonly requestId: string;

  constructor(message: string, details: DesignerRequestErrorDetails) {
    super(message);
    this.name = 'DesignerRequestError';
    this.code = details.code;
    this.operation = details.operation;
    this.status = details.status;
    this.retryable = details.retryable;
    this.requestId = details.requestId;
  }
}

export interface DesignerJsonRequestOptions extends RequestInit {
  operation: string;
  requestId?: string;
  timeoutMs?: number;
  readOnly?: boolean;
  retries?: number;
  validate?: (payload: unknown) => boolean;
}

const RETRYABLE_STATUSES = new Set([502, 503, 504]);

export function createDesignerRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `veyop-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function messageFromPayload(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== 'object') return fallback;
  const row = payload as Record<string, unknown>;
  const message = String(row.msg ?? row.message ?? '').trim();
  if (message) return message;
  const nested = row.data;
  if (nested && typeof nested === 'object') {
    const nestedRow = nested as Record<string, unknown>;
    const nestedMessage = String(nestedRow.msg ?? nestedRow.message ?? '').trim();
    if (nestedMessage) return nestedMessage;
  }
  return fallback;
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function requestAttempt<T>(
  url: string,
  options: DesignerJsonRequestOptions,
  requestId: string,
): Promise<T> {
  const timeoutMs = Math.max(1, options.timeoutMs ?? 15_000);
  const controller = new AbortController();
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  const externalSignal = options.signal;
  const abortFromExternal = () => controller.abort();
  externalSignal?.addEventListener('abort', abortFromExternal, { once: true });

  try {
    const {
      operation: _operation,
      requestId: _requestId,
      timeoutMs: _timeoutMs,
      readOnly: _readOnly,
      retries: _retries,
      validate: _validate,
      ...fetchOptions
    } = options;
    const headers = new Headers(fetchOptions.headers);
    headers.set('X-Veyop-Request-ID', requestId);
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
      credentials: fetchOptions.credentials ?? 'same-origin',
      headers,
    });
    const text = await response.text();
    const trimmed = text.trim();
    let payload: unknown = null;

    if (trimmed !== '') {
      try {
        payload = JSON.parse(trimmed);
      } catch {
        throw new DesignerRequestError('Server returned invalid JSON.', {
          code: 'invalid_json',
          operation: options.operation,
          status: response.status,
          retryable: false,
          requestId,
        });
      }
    }

    if (!response.ok) {
      throw new DesignerRequestError(
        messageFromPayload(payload, `Request failed (HTTP ${response.status}).`),
        {
          code: 'http_error',
          operation: options.operation,
          status: response.status,
          retryable: RETRYABLE_STATUSES.has(response.status),
          requestId,
        },
      );
    }
    if (trimmed === '') {
      throw new DesignerRequestError('Server returned an empty response.', {
        code: 'empty_response',
        operation: options.operation,
        status: response.status,
        retryable: false,
        requestId,
      });
    }
    if (options.validate && !options.validate(payload)) {
      throw new DesignerRequestError('Server returned an unexpected response.', {
        code: 'invalid_response',
        operation: options.operation,
        status: response.status,
        retryable: false,
        requestId,
      });
    }
    return payload as T;
  } catch (reason) {
    if (reason instanceof DesignerRequestError) throw reason;
    if (timedOut) {
      throw new DesignerRequestError('Request timed out. Please try again.', {
        code: 'timeout',
        operation: options.operation,
        status: 0,
        retryable: true,
        requestId,
      });
    }
    if (externalSignal?.aborted || (reason instanceof DOMException && reason.name === 'AbortError')) {
      throw new DesignerRequestError('Request was cancelled.', {
        code: 'aborted',
        operation: options.operation,
        status: 0,
        retryable: false,
        requestId,
      });
    }
    throw new DesignerRequestError('Connection failed. Please check your network.', {
      code: 'network_error',
      operation: options.operation,
      status: 0,
      retryable: true,
      requestId,
    });
  } finally {
    clearTimeout(timeout);
    externalSignal?.removeEventListener('abort', abortFromExternal);
  }
}

export async function requestDesignerJson<T>(
  url: string,
  options: DesignerJsonRequestOptions,
): Promise<T> {
  const requestId = options.requestId || createDesignerRequestId();
  const retries = options.readOnly ? Math.max(0, options.retries ?? 1) : 0;
  let attempt = 0;

  while (true) {
    try {
      return await requestAttempt<T>(url, options, requestId);
    } catch (reason) {
      const error = reason as DesignerRequestError;
      if (attempt >= retries || !error.retryable || error.code === 'aborted') throw error;
      attempt += 1;
      await wait(250 + Math.floor(Math.random() * 151));
    }
  }
}

export function publicDesignerError(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (error instanceof DesignerRequestError) return error.message;
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
}
